import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError, useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Loader2, Copy, Check, Gift, Users, Sparkles, Share2, Mail, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

export default function Referrals() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/referrals/me")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  const link = data?.referral_code
    ? `${window.location.origin}/register?ref=${data.referral_code}`
    : "";

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      toast.success("Copié dans le presse-papiers");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Impossible de copier");
    }
  };

  const shareText = `Bonjour, j'utilise DoctoSpa pour diagnostiquer les pannes de mon spa. Ça vaut le coup, et avec mon code tu bénéficies d'un mois offert. Mon code : ${data?.referral_code}`;
  const shareUrl = link ? `${link}` : window.location.origin;

  const openShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "DoctoSpa", text: shareText, url: shareUrl });
      } catch {}
    } else {
      copy(link, "share");
    }
  };

  if (!data) {
    return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const premiumUntilFmt = data.premium_until
    ? new Date(data.premium_until).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <div className="mx-auto max-w-5xl px-6 lg:px-10 py-12" data-testid="referrals-page">
      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden bg-secondary text-white p-8 lg:p-12 mb-10">
        <div className="absolute inset-0 grid-pattern-dense opacity-15" />
        <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-xs font-semibold">
              <Gift className="h-3.5 w-3.5" /> Programme de parrainage
            </div>
            <h1 className="mt-4 font-display text-3xl lg:text-4xl font-black tracking-tighter leading-[1.1]">
              Parrainez un ami,<br />
              gagnez <span className="text-primary">1 mois Premium.</span>
            </h1>
            <p className="mt-4 text-white/70 text-sm max-w-md leading-relaxed">
              Pour chaque ami qui crée un compte avec votre code, vous recevez
              automatiquement 30 jours d'accès Premium (rapports illimités).
            </p>
          </div>
          <div>
            <div className="eyebrow text-white/50">Votre code personnel</div>
            <div className="mt-3 flex items-center gap-3">
              <div className="font-display font-black text-5xl lg:text-6xl tracking-widest flex-1 min-w-0 truncate" data-testid="my-referral-code">
                {data.referral_code || "—"}
              </div>
              {data.referral_code && (
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => copy(data.referral_code, "code")}
                  className="bg-white text-secondary hover:bg-white/90 rounded-full shrink-0"
                  data-testid="copy-code-btn"
                >
                  {copied === "code" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
        <Card className="p-6 border-black/10" data-testid="kpi-referrals">
          <div className="flex items-start justify-between">
            <div className="eyebrow">Filleuls</div>
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-3 font-display text-4xl font-black">{data.referral_count}</div>
          <p className="mt-1 text-xs text-muted-foreground">amis parrainés</p>
        </Card>
        <Card className="p-6 border-black/10" data-testid="kpi-days-earned">
          <div className="flex items-start justify-between">
            <div className="eyebrow">Jours Premium gagnés</div>
            <Gift className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="mt-3 font-display text-4xl font-black">{(data.referral_count || 0) * 30}</div>
          <p className="mt-1 text-xs text-muted-foreground">via parrainage</p>
        </Card>
        <Card className="p-6 border-black/10" data-testid="kpi-premium-until">
          <div className="flex items-start justify-between">
            <div className="eyebrow">Premium jusqu'au</div>
            <Sparkles className="h-4 w-4 text-violet-600" />
          </div>
          <div className="mt-3 font-display text-2xl font-bold">
            {premiumUntilFmt || <span className="text-muted-foreground">—</span>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {user?.subscription_status === "active" && "+ abonnement actif"}
          </p>
        </Card>
      </div>

      {/* Share link */}
      <Card className="p-6 lg:p-8 border-black/10 mb-10">
        <div className="eyebrow">Votre lien de parrainage</div>
        <h2 className="mt-2 font-display text-xl font-semibold">Partagez-le en 1 clic</h2>
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <Input value={link} readOnly className="font-mono text-sm bg-muted/30" data-testid="referral-link" />
          <Button onClick={() => copy(link, "link")} variant="outline" className="rounded-full" data-testid="copy-link-btn">
            {copied === "link" ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied === "link" ? "Copié" : "Copier le lien"}
          </Button>
          <Button onClick={openShare} className="rounded-full" data-testid="share-btn">
            <Share2 className="h-4 w-4 mr-2" /> Partager
          </Button>
        </div>
        <div className="mt-6 flex flex-wrap gap-2 text-xs">
          <a
            href={`mailto:?subject=${encodeURIComponent("DoctoSpa - diagnostic gratuit de ton spa")}&body=${encodeURIComponent(shareText + "\n\n" + shareUrl)}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/10 hover:border-primary/40 hover:text-primary transition-colors"
            data-testid="share-email"
          >
            <Mail className="h-3 w-3" /> Email
          </a>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-black/10 hover:border-primary/40 hover:text-primary transition-colors"
            data-testid="share-whatsapp"
          >
            <MessageSquare className="h-3 w-3" /> WhatsApp
          </a>
        </div>
      </Card>

      {/* History */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold">Historique des parrainages</h2>
        </div>
        {data.referrals.length === 0 ? (
          <Card className="p-12 border-dashed border-black/15 text-center">
            <Users className="h-10 w-10 mx-auto text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground max-w-sm mx-auto">
              Aucun parrainage pour l'instant. Partagez votre code pour commencer à gagner du Premium !
            </p>
          </Card>
        ) : (
          <Card className="border-black/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 border-b border-black/5">
                <tr className="text-left">
                  <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Date</th>
                  <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Filleul</th>
                  <th className="px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Récompense</th>
                </tr>
              </thead>
              <tbody>
                {data.referrals.map((r) => (
                  <tr key={r.id} className="border-b border-black/5 last:border-0">
                    <td className="px-6 py-4 text-muted-foreground">
                      {new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{r.friend_name || "Filleul"}</div>
                      <div className="text-xs text-muted-foreground font-mono">{r.friend_email_masked}</div>
                    </td>
                    <td className="px-6 py-4">
                      {r.reward_granted
                        ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">+30 jours Premium</Badge>
                        : <Badge variant="outline">En attente</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>

      {/* Promo code hint */}
      <div className="mt-16 rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 p-8 text-center">
        <div className="eyebrow text-primary">Bonus</div>
        <h3 className="mt-2 font-display text-xl font-semibold">Un code promo ? Utilisez-le au paiement.</h3>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
          Sur la page Stripe Checkout, cliquez sur « Ajouter un code promo ».
          Essayez <span className="font-mono font-bold text-primary">SPA10</span> pour 10% de réduction.
        </p>
        <Link to="/pricing"><Button className="mt-6 rounded-full" data-testid="referrals-to-pricing">Voir les tarifs</Button></Link>
      </div>
    </div>
  );
}
